import React from 'react';
import { connect } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { selectAreFontsReady } from '@ff-os/pdp-shared-redux/slices/fontsSlice';
import { selectLanguage } from '@ff-os/pdp-shared-redux/slices/globalSlice';
import { Spaces } from '@ff-os/rendering-shared/coordinates';
import { PaintablesRenderer } from '@ff-os/shared-react/components/paintables-renderer/PaintablesRenderer';
import { Size } from '@ff-os/shared-utils/types/Size';
import { selectVariablePaintables } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { Paintable, Photo } from 'ff-canvas';

const _PhotoEditPreview = ({ paintable, language, fontsReady, spaces }: Props) => {
    return (
        <PaintablesRenderer
            fontsReady={fontsReady}
            paintables={[paintable]}
            language={language}
            spaces={spaces} />
    );
};

type OwnProps = {
    variableIds: string[];
    imageSize: Size;
};

type Props = OwnProps & {
    fontsReady: boolean;
    language: string;
    spaces: Spaces;
    paintable: Paintable;
};

const mapStateToProps = (state: GiftPdpState, ownProps: OwnProps) => ({
    fontsReady: selectAreFontsReady(state),
    language: selectLanguage(state),
    paintable: selectPhotoPaintableWithoutTransforms(state, ownProps.variableIds, ownProps.imageSize),
    spaces: selectPhotoSpaces(ownProps.imageSize)
});

export const PhotoEditPreview = connect(mapStateToProps)(_PhotoEditPreview);

const selectPhotoPaintable = createSelector([
    (state: GiftPdpState) => selectVariablePaintables(state, 'mockup'),
    (state: GiftPdpState, photoId: string) => photoId
], (paintables: Paintable[], photoId: string) => {
    return paintables.find(p => p.id === photoId)! as Photo;
});

const selectPhotoPaintableWithoutTransforms = createSelector([
    (state: GiftPdpState, variableIds: string[]) => selectPhotoPaintable(state, variableIds[0]),
    (state: GiftPdpState, variableIds: string[], imageSize: Size) => imageSize
], (photo: Photo, imageSize: Size): Photo => ({

    ...photo,

    x: -0.5 * imageSize.width,
    y: -0.5 * imageSize.height,
    width: imageSize.width,
    height: imageSize.height,

    clipPaths: [],
    clipViewBoxWidth: 0,
    clipViewBoxHeight: 0,

    innerAngle: 0,
    innerOffsetX: 0,
    innerOffsetY: 0,
    innerZoom: 1
}));

const selectPhotoSpaces = createSelector([
    (imageSize: Size) => imageSize
], (imageSize: Size) => ({

    sceneWidth: imageSize.width,
    sceneHeight: imageSize.height,

    designAreaX: 0,
    designAreaY: 0,
    designAreaWidth: imageSize.width,
    designAreaHeight: imageSize.height,

    designWidth: imageSize.width,
    designHeight: imageSize.height
}));